import { HttpInterceptorFn, HttpRequest, HttpHandlerFn } from '@angular/common/http';
import { environment } from '../../environments/environment';

// Storage key for session (must match AuthService)
const STORAGE_KEY = 'ams_session';

/**
 * HTTP interceptor that adds the JWT access token and tenant ID to API requests.
 * Also transforms relative API URLs to absolute URLs using the environment config.
 *
 * Tenant ID and auth token are only sent when an active session exists.
 * Anonymous endpoints (invitation, password reset, public settings) must not
 * depend on the frontend sending a tenant header — the backend resolves
 * tenant context from its own data (token records, domain lookup, etc.).
 *
 * Note: We read from localStorage directly to avoid circular dependency with AuthService.
 */
export const authInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
) => {

  // Only intercept API calls (not external URLs or assets)
  if (!req.url.startsWith('/api') && !req.url.startsWith('api/')) {
    return next(req);
  }

  // Build the full API URL
  let url = req.url;
  if (url.startsWith('/api')) {
    url = `${environment.apiBaseUrl}${url.substring(4)}`; // Remove /api prefix, use base URL
  } else if (url.startsWith('api/')) {
    url = `${environment.apiBaseUrl}/${url.substring(4)}`;
  }

  // Only send auth headers when an active session exists
  const headers: Record<string, string> = {};

  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const session = JSON.parse(stored);
      if (session.accessToken) {
        headers['Authorization'] = `Bearer ${session.accessToken}`;
      }
      if (session.user?.tenantId) {
        headers['X-Tenant-Id'] = session.user.tenantId;
      }
    }
  } catch (e) {
    console.warn('[AuthInterceptor] Failed to read session from localStorage');
  }

  const authReq = req.clone({
    url,
    setHeaders: headers,
  });

  return next(authReq);
};
